/**
 * NotificationPopper - to display top 5 notification to user
 *
 * @version 1.0.1
 * @author [Karthick Shanmugam](https://github.com/karthickShanmugam0689)
 */
import * as React from 'react'
import {graphql, compose, withApollo} from 'react-apollo'
import {
  getDrupalNotifications,
  readAllNotificationsMutation,
  getDrupalNotificationsCount,
} from '../../queries/notificationQuery'
import {getContext} from 'recompose'
import {targetPropTypes} from '../../commonPropsType'
import {IProps} from './NotificationPopper.d'
import SXTNotificationList from '../common/SXTNotification/SXTNotificationList'
import {NavLink} from 'react-router-dom'
import {INotification} from '../common/SXTNotification/SXTNotification.d'
import './NotificationPopper.scss'
import {checkObjProps} from '../../utils/genericUtils'
import Loader from '../common/Loader/Loader'
import DraftsIcon from '@material-ui/icons/Drafts'
import IconButton from '@material-ui/core/IconButton'
import {fetchDrupalNotifications} from './utils.Notifications'

const NOTIFICATIONS_ALLOWED_IN_POPPER = 5
const NOTIFICATION_FETCH_LIMIT = 25

const getRequiredNotifications = (
  notificationList: INotification[] | null | undefined
) => {
  if (!notificationList?.length) return []
  return notificationList.slice(0, NOTIFICATIONS_ALLOWED_IN_POPPER)
}

const NotificationPopper = (props: IProps) => {
  const {
    error,
    isDrupalLoading,
    handleToggle,
    client,
    drupalNotifications,
    acknowledgedrupNotification,
  } = props

  const [notificationsToRead, setNotificationsToRead] = React.useState<
    INotification[]
  >([])
  const [isMerging, setIsMerging] = React.useState(false)

  const sortedNotifications = React.useMemo(() => {
    if (!drupalNotifications?.length) return []

    try {
      const mentionsArray = fetchDrupalNotifications(drupalNotifications)
      return mentionsArray.sort((e1, e2) => {
        const date1 = new Date(e1.submittedDate).getTime()
        const date2 = new Date(e2.submittedDate).getTime()
        return date2 - date1
      })
    } catch (err) {
      console.error('[NotificationPopper] Error processing notifications:', err)
      return []
    }
  }, [drupalNotifications])

  React.useEffect(() => {
    let mounted = true
    const updateNotifications = async () => {
      if (!mounted) return
      setIsMerging(true)
      try {
        const finalNotifications = getRequiredNotifications(sortedNotifications)
        setNotificationsToRead(finalNotifications)
      } catch (err) {
        console.error('[NotificationPopper] Error updating notifications:', err)
      } finally {
        setIsMerging(false)
      }
    }

    updateNotifications()
    return () => {
      mounted = false
    }
  }, [sortedNotifications])

  const markAllNotificationsAsRead = async () => {
    try {
      await acknowledgedrupNotification()
      handleToggle(false)
    } catch (err) {
      console.error(
        '[NotificationPopper] Error marking notifications as read:',
        err
      )
    }
  }

  if (error) {
    return <div className="error-block">Error! {error.message}</div>
  }

  return (
    <div className="notification-popper">
      {isDrupalLoading || isMerging ? (
        <Loader isInContainer={true} />
      ) : (
        <>
          {notificationsToRead.length > 0 && (
            <IconButton
              className="mark-read"
              onClick={markAllNotificationsAsRead}
            >
              <DraftsIcon color="inherit" fontSize="small" />
              Mark all as read
            </IconButton>
          )}
          <SXTNotificationList
            notifications={notificationsToRead}
            isLighterView={true}
          />
          {notificationsToRead.length > 4 && (
            <NavLink
              to="/notification/all"
              className="view-all"
              onClick={() => handleToggle(false)}
            >
              View All
            </NavLink>
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(
  compose(
    withApollo,
    getContext(targetPropTypes),
    graphql(readAllNotificationsMutation, {
      name: 'acknowledgedrupNotification',
    }),
    graphql(getDrupalNotifications, {
      options: () => ({
        variables: {
          since: -1,
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
      }),
      props: ({data: {error, loading, notifications = []}}: any) => {
        return {
          error,
          isDrupalLoading: loading,
          drupalNotifications: notifications,
        }
      },
    })
  )(NotificationPopper)
)
